/**
 * Full Send Homepage - Visual Polish JS
 * Scroll progress bar, varied section animations, parallax
 */

(function () {
  'use strict';

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ===========================================
  // SCROLL PROGRESS BAR
  // Thin coral line at the top of the viewport
  // Uses scaleX for compositor-friendly animation
  // ===========================================

  function initScrollProgress() {
    var bar = document.createElement('div');
    bar.className = 'fs-scroll-progress';
    bar.setAttribute('aria-hidden', 'true');
    document.body.appendChild(bar);

    var ticking = false;

    function updateProgress() {
      var scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      var docHeight = document.documentElement.scrollHeight - window.innerHeight;
      var progress = docHeight > 0 ? scrollTop / docHeight : 0;
      bar.style.transform = 'scaleX(' + Math.min(progress, 1) + ')';
      ticking = false;
    }

    window.addEventListener('scroll', function () {
      if (!ticking) {
        requestAnimationFrame(updateProgress);
        ticking = true;
      }
    }, { passive: true });

    updateProgress();
  }


  // ===========================================
  // SECTION VISIBILITY OBSERVER
  // Adds .is-visible to sections when they scroll into view
  // Enables the varied CSS animations in fs-polish.css
  // ===========================================

  function initSectionAnimations() {
    if (prefersReducedMotion) return;

    var sections = document.querySelectorAll(
      '.fs-benefits--v3, .fs-how-it-works--v3, .fs-benefits-bar'
    );

    if (!sections.length) return;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.15,
      rootMargin: '0px 0px -40px 0px'
    });

    sections.forEach(function (section) {
      observer.observe(section);
    });
  }


  // ===========================================
  // HERO PARALLAX
  // Subtle depth effect on the hero background
  // Uses IntersectionObserver to only run when hero is visible
  // ===========================================

  function initHeroParallax() {
    if (prefersReducedMotion) return;

    var hero = document.querySelector('.fs-hero-section, #hero');
    if (!hero) return;

    var bg = hero.querySelector('.fs-hero__bg, .fs-hero__video, img');
    if (!bg) return;

    var scrollActive = false;
    var ticking = false;
    var heroBottom = hero.offsetTop + hero.offsetHeight;

    function updateParallax() {
      var scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      if (scrollTop < heroBottom) {
        var offset = scrollTop * 0.25;
        bg.style.transform = 'translateY(' + offset + 'px) scale(1.05)';
      }
      ticking = false;
    }

    function onScroll() {
      if (!ticking) {
        requestAnimationFrame(updateParallax);
        ticking = true;
      }
    }

    // Only attach scroll listener when hero is in view
    var visibilityObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting && !scrollActive) {
          scrollActive = true;
          window.addEventListener('scroll', onScroll, { passive: true });
        } else if (!entry.isIntersecting && scrollActive) {
          scrollActive = false;
          window.removeEventListener('scroll', onScroll);
        }
      });
    }, { threshold: 0 });

    visibilityObserver.observe(hero);
  }


  // ===========================================
  // STRENGTH SELECTOR — SLIDING INDICATOR
  // Creates a sliding pill behind the selected option
  // ===========================================

  function initStrengthIndicators() {
    var selectors = document.querySelectorAll('.fs-flavors__strengths--v3');
    if (!selectors.length) return;

    selectors.forEach(function (selector) {
      var pills = selector.querySelectorAll('.fs-flavors__strength-pill--v3');
      if (pills.length < 2) return;

      // Create the sliding indicator element
      var indicator = document.createElement('div');
      indicator.className = 'fs-flavors__strength-indicator';
      selector.style.position = 'relative';
      selector.appendChild(indicator);

      function moveIndicator(target, animate) {
        var selectorRect = selector.getBoundingClientRect();
        var targetRect = target.getBoundingClientRect();
        var offsetLeft = targetRect.left - selectorRect.left;
        indicator.style.width = targetRect.width + 'px';
        indicator.style.transform = 'translateX(' + offsetLeft + 'px)';
        if (!animate) {
          indicator.style.transition = 'none';
          // Force reflow then restore transition
          indicator.offsetHeight;
          indicator.style.transition = '';
        }
      }

      // Position on the initially selected pill, then hand the "selected"
      // visual over from the pill's own background to the indicator
      // (.has-indicator, see fs-polish.css). Same pixels either way, so no flash.
      var selected = selector.querySelector('.is-selected');
      if (selected) {
        moveIndicator(selected, false);
      }
      selector.classList.add('has-indicator');

      // Listen for clicks to move the indicator
      pills.forEach(function (pill) {
        pill.addEventListener('click', function () {
          if (pill.disabled || pill.classList.contains('is-unavailable')) return;
          moveIndicator(pill, true);
        });
      });

      // Re-position on resize
      var resizeTimer;
      window.addEventListener('resize', function () {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(function () {
          var current = selector.querySelector('.is-selected');
          if (current) moveIndicator(current, false);
        }, 100);
      });
    });
  }


  // ===========================================
  // MOBILE SCROLL PEEK — EDGE FADE DETECTION
  // Hides the right-edge gradient when scrolled to end
  // ===========================================

  function initMobileScrollPeek() {
    if (window.innerWidth >= 640) return;

    var container = document.querySelector('.fs-flavors__container--v3');
    var grid = document.querySelector('.fs-flavors__grid--v3');
    if (!container || !grid) return;

    function checkScrollEnd() {
      var scrollLeft = grid.scrollLeft;
      var maxScroll = grid.scrollWidth - grid.clientWidth;
      if (maxScroll > 0 && scrollLeft >= maxScroll - 10) {
        container.classList.add('is-scrolled-end');
      } else {
        container.classList.remove('is-scrolled-end');
      }
    }

    grid.addEventListener('scroll', checkScrollEnd, { passive: true });
    checkScrollEnd();
  }


  // ===========================================
  // INITIALIZE
  // ===========================================

  function init() {
    initScrollProgress();
    initSectionAnimations();
    initHeroParallax();
    initStrengthIndicators();
    initMobileScrollPeek();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
